import { useEffect, useRef, useState } from 'react';
import { fetchApprovals, ApprovalFetchItem } from '@/api/approvals';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@chainlit/react-client';

type Notification = {
  id: string;
  title: string;
  description?: string;
  createdAt: string;
  read?: boolean;
  source?: 'approval';
  incidentId?: string;
  updateId?: number;
};

interface NotificationsDropdownProps {
  initial?: Notification[];
}

export function NotificationsDropdown({ initial = [] }: NotificationsDropdownProps) {
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>(initial);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  // const userRole = (user?.metadata as any)?.role || '';

  const incident_comms_admin = (user?.metadata as any)?.incident_comms_admin || false;
  const userRole = incident_comms_admin ? 'incident_comms_admin' : 'incident_comms_user';


  useEffect(() => {
    if (!incident_comms_admin) return;

    let mounted = true;
    let ws: WebSocket | null = null;
    let reconnectTimer: number | null = null;

    const baseWs =
      import.meta.env.MODE === 'production'
        ? 'wss://askawatch.cbre.com'
        : import.meta.env.MODE === 'staging'
        ? 'wss://askawatch-test.cbre.com'
        : 'ws://localhost:8000';

    const applyApprovals = (approvals: ApprovalFetchItem[]) => {
      setNotifications(prev => {
        const byId = new Map(prev.map(p => [p.id, p])); // keep existing
        for (const a of approvals) {
          const incidentNumber = a.incident_number || a.incident_id || 'N/A';
          const id = String(a.approval_id ?? a.update_id);
          const existing = byId.get(id);
          byId.set(id, {
            id,
            title: `Incident ${incidentNumber} needs review`,
            description: a.update_text?.slice(0, 160),
            createdAt: a.submitted_at || new Date().toISOString(),
            // preserve read if it existed
            read: existing?.read ?? false,
            source: 'approval',
            incidentId: incidentNumber,
            updateId: a.update_id
          });
        }
        // Return as array sorted newest first by createdAt
        return Array.from(byId.values()).sort(
          (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
      });
    };

    const connect = () => {
      ws = new WebSocket(`${baseWs}/api/approvals/ws/approvals`);
      ws.onopen = () => {
        setError(null);
      };
      ws.onmessage = (ev) => {
        if (!mounted || !ev.data) return;
        try {
          const payload = JSON.parse(ev.data);

          // Add/merge pending approvals
          if (Array.isArray(payload.approvals)) {
            applyApprovals(payload.approvals);
          } else if (payload.approval) {
            applyApprovals([payload.approval]);
          }

          // NEW: resolve removal
          const resolved = payload.resolved as { update_id?: number; approval_id?: number } | undefined;
          if (resolved) {
            const rid = String(resolved.approval_id ?? resolved.update_id ?? '');
            setNotifications(prev => {
              // remove by id or by updateId field
              return prev.filter(n => {
                if (rid && n.id === rid) return false;
                if (resolved.update_id != null && n.updateId === resolved.update_id) return false;
                return true;
              });
            });
          }
        } catch {
          // ignore malformed
        }
      };
      ws.onerror = () => {
        setError('Realtime connection error');
      };
      ws.onclose = () => {
        if (!mounted) return;
        if (reconnectTimer) clearTimeout(reconnectTimer);
        reconnectTimer = window.setTimeout(connect, 3000);
      };
    };

    connect();

    return () => {
      mounted = false;
      if (ws) ws.close();
      if (reconnectTimer) clearTimeout(reconnectTimer);
    };
  }, [userRole]);

  useEffect(() => {    
    function onDocClick(e: MouseEvent) {
      if (!panelRef.current) return;
      if (!panelRef.current.contains(e.target as Node)) setOpen(false);
    }
    if (open) document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open, userRole]);

  const unread = notifications.filter(n => !n.read).length;

  function toggle() {
    setOpen(o => !o);
  }

  function markAllRead() {
    setNotifications(ns => ns.map(n => ({ ...n, read: true })));
  }

  function refresh() {
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const r = await fetchApprovals({ limit: 50 });
        const approvals = r.approvals || [];
        setNotifications(approvals.map(a => {
          const incidentNumber = a.incident_number || a.incident_id || 'N/A';
          return {
            id: String(a.approval_id ?? a.update_id),
            title: `Incident ${incidentNumber} needs review`,
            description: a.update_text?.slice(0, 160),
            createdAt: a.submitted_at || new Date().toISOString(),
            read: false,
            source: 'approval'
          };
        }));
      } catch (e: any) {
        setError(e.message || 'Failed to refresh');
      } finally {
        setLoading(false);
      }
    })();
  }

  function handleClick(n: Notification) {
    // Only redirect for admin
    if (userRole === 'incident_comms_admin' && n.incidentId && n.updateId != null) {
      navigate(`/incidentcommunication?incident=${encodeURIComponent(n.incidentId)}&update=${n.updateId}`);
      setNotifications(list =>
        list.map(x => x.id === n.id ? { ...x, read: true } : x)
      );
      setOpen(false);
    } else {
      // Non-admin mark read only
      setNotifications(list =>
        list.map(x => x.id === n.id ? { ...x, read: true } : x)
      );
    }
  }

  return (
    <div className="relative" ref={panelRef}>
      <button
        type="button"
        onClick={toggle}
        className="relative flex h-9 w-9 items-center justify-center rounded-md border border-white/10 bg-white/5 hover:bg-white/10 transition"
        aria-haspopup="true"
        aria-expanded={open}
        aria-label="Notifications"
      >
        {/* Bell Icon */}
        <svg
          className="h-5 w-5 text-white"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 3c-2.5 0-4.5 2-4.5 4.5v1.03c0 .63-.22 1.24-.62 1.72l-.89 1.12A2.8 2.8 0 0 0 5 13.31V15a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-1.69c0-.63-.22-1.24-.62-1.72l-.89-1.12a2.8 2.8 0 0 1-.62-1.72V7.5C16.5 5 14.5 3 12 3Zm0 0v0m-2 15a2 2 0 1 0 4 0h-4Z"
          />
        </svg>
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] rounded-full bg-red-500 px-1.5 py-0.5 text-[10px] font-medium leading-none text-white">
            {unread}
          </span>
        )}
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 mt-2 w-80 rounded-lg border border-white/10 bg-[#0F281F]/95 backdrop-blur p-3 shadow-lg"
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-white">Notifications</span>
            <div className="flex gap-2">
              <button
                onClick={refresh}
                className="text-xs text-white/60 hover:text-white"
              >
                Refresh
              </button>
              <button
                onClick={markAllRead}
                className="text-xs text-white/60 hover:text-white"
              >
                Mark all read
              </button>
            </div>
          </div>
          {loading && (
            <p className="text-xs text-white/50 mb-2">Loading approvals...</p>
          )}
          {error && (
            <p className="text-xs text-red-400 mb-2">{error}</p>
          )}
          <div className="max-h-64 overflow-y-auto space-y-2">
            {notifications.length === 0 && !loading && !error && (
              <p className="text-xs text-white/50">No notifications.</p>
            )}
            {userRole == 'incident_comms_admin' && notifications.map(n => (
              <button
                key={n.id}
                type="button"
                onClick={() => handleClick(n)}
                className={`w-full text-left rounded-md border border-white/10 p-2 transition ${
                  !n.read ? 'bg-white/5 hover:bg-white/10' : 'bg-transparent hover:bg-white/5'
                }`}
              >
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold text-white">{n.title}</p>
                  {!n.read && (
                    <span className="ml-2 h-2 w-2 rounded-full bg-green-400" />
                  )}
                </div>
                {n.description && (
                  <p className="mt-0.5 text-[11px] text-white/60 line-clamp-3">
                    {n.description}
                  </p>
                )}
                <p className="mt-0.5 text-[10px] text-white/40">
                  {new Date(n.createdAt).toLocaleString()}
                </p>
                {userRole === 'incident_comms_admin' && n.incidentId && n.updateId != null && (
                  <p className="mt-0.5 text-[10px] text-[#18E78F]/70">
                    Click to open incident {n.incidentId} (update #{n.updateId})
                  </p>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}